import React, { useEffect, useMemo, useRef, useState } from 'react';

import RegisterCombobox from '../admission/RegisterCombobox';
import { lookupPincode, prefetchPincodeShard, listStates, listDistricts } from '../../utils/pincodeLookup';
import { ADDRESS_FIELDS, PINCODE_ERROR, formatAddressLines } from '../../utils/patientAddress';

/**
 * AddressBlock — the shared six-field structured patient address (Register
 * Visit, IPD quick register, IPD Patient Details). All fields optional; only
 * the pincode format is checked, and only when something is entered (OR-122).
 *
 * mode="collapsed" = Variant A (OR-125): one row ("Add address (optional)" or
 * the 2-line summary + Edit) that opens the fields in place; "Done" closes.
 * mode="open" = the fields always shown (Patient Details).
 *
 * INVARIANT: a field's value changes ONLY by staff input or the pincode fill
 * rule (OR-133/135): a 6-digit pincode fills District/State only when that
 * field is empty or still holds the value the pincode itself filled — never a
 * typed or ABHA value. The block never seeds/clears anything on its own (the
 * host seeds Address line from the legacy one-line address).
 *
 * Source marks (OR-126, D6): plain grey "ABHA" / amber "Edited" next to the
 * label. `abhaValues` (new patient, in-form) wins: equal to ABHA = "ABHA",
 * different = "Edited". Otherwise the server `source` map, with a field staff
 * change in this block turning "Edited". No marks when neither is present.
 * Pincode-filled values carry no mark.
 */

const PINCODE_RE = /^[0-9]{6}$/;
const LIST_CAP = 8;

const LEGACY_HINT = 'Address saved in the old format. Please fill the fields below when you can.';
const ABDM_NOTE = "Address isn't sent to ABDM; edits only change what the app shows and prints. A new ABHA verify replaces it again.";

const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800';
const titleCls = 'block text-xs font-semibold text-slate-500 dark:text-slate-400';
const subLabelCls = 'flex flex-wrap items-center gap-x-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const hintCls = 'text-[11.5px] text-slate-500 dark:text-slate-400 mt-1';

// Layout (mockup): wide = Pincode + Address line / Town + Sub-district /
// District + State on a 6-col grid; under 400 px of block width it stacks to
// Pincode / Address line / Town + Sub-district / District + State.
// Class strings stay LITERAL (Tailwind scans source text; no interpolation).
const FIELD_UI = {
  address_pincode: { label: 'Pincode', placeholder: '6 digits', span: 'col-span-2', slug: 'pincode' },
  address_line: { label: 'Address line', placeholder: 'House / Flat no., street, locality, landmark', span: 'col-span-2 [@container(min-width:400px)]:col-span-4', max: 500, slug: 'line' },
  address_town: { label: 'Village / Town / City', placeholder: 'e.g. Rampur', span: 'col-span-1 [@container(min-width:400px)]:col-span-3', max: 100, slug: 'town' },
  address_sub_district: { label: 'Sub-district (Tehsil)', placeholder: 'Sub-district', span: 'col-span-1 [@container(min-width:400px)]:col-span-3', max: 100, slug: 'sub-district' },
  address_district: { label: 'District', placeholder: 'Search district', max: 100, span: 'col-span-1 [@container(min-width:400px)]:col-span-3', slug: 'district' },
  address_state: { label: 'State', placeholder: 'Search state', max: 100, span: 'col-span-1 [@container(min-width:400px)]:col-span-3', slug: 'state' },
};
const LAYOUT = ['address_pincode', 'address_line', 'address_town', 'address_sub_district', 'address_district', 'address_state'];

const str = (v) => (v == null ? '' : String(v)).trim();
const toOptions = (names) => names.map((n) => ({ name: n, display_name: n }));

export default function AddressBlock({
  value, onChange, source, abhaValues, legacyAddress, mode = 'collapsed',
  disabled = false, idPrefix = 'addr', testIdPrefix = 'addr',
}) {
  const [expanded, setExpanded] = useState(false);
  const [pinTouched, setPinTouched] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [districts, setDistricts] = useState([]);
  const pinInputRef = useRef(null);
  const autoRef = useRef({}); // values the pincode lookup itself filled (this mount)
  const ownRef = useRef({});  // last value per field this block emitted / saw
  const baseRef = useRef({}); // value per field as it last arrived from the host
  const srcSeenRef = useRef({}); // source mark per field as last seen
  const districtsForRef = useRef(null);
  const aliveRef = useRef(true);
  // Re-arm on (re)mount: StrictMode runs cleanup then re-mounts (QA-ADDR-1).
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  const srcMap = source || {};
  const hasMarks = !!abhaValues || Object.keys(srcMap).length > 0;

  // Host-originated changes (patient loaded, ABHA replace = value or source
  // change) reset the baseline "Edited" is measured against; the block's own
  // emits do not.
  ADDRESS_FIELDS.forEach((f) => {
    const v = str(value?.[f]);
    if (ownRef.current[f] !== v || srcSeenRef.current[f] !== srcMap[f]) {
      ownRef.current[f] = v; baseRef.current[f] = v; srcSeenRef.current[f] = srcMap[f];
    }
  });

  const isAbhaOrigin = (f, cur, src, abha) => {
    const v = str(cur?.[f]);
    if (!v) return false;
    if (abha) return v === str(abha[f]);
    return src[f] === 'ABHA' && v === baseRef.current[f];
  };
  // Latest props for the async pincode resolution (fill rule must judge the
  // values as they are when the lookup lands, not when it started).
  const latest = useRef({});
  latest.current = { value, srcMap, abhaValues, onChange };

  const markFor = (f) => {
    if (isAbhaOrigin(f, value, srcMap, abhaValues)) return 'ABHA';
    if (!hasMarks) return null;
    const v = str(value?.[f]);
    if (v && str(autoRef.current[f]) === v) return null;
    if (abhaValues) return v !== str(abhaValues[f]) ? 'Edited' : null;
    if (srcMap[f] === 'ABHA' || srcMap[f] === 'EDITED') return 'Edited';
    return v !== baseRef.current[f] ? 'Edited' : null;
  };

  const emit = (next) => {
    const out = {};
    ADDRESS_FIELDS.forEach((f) => { out[f] = next[f] ?? ''; ownRef.current[f] = str(out[f]); });
    latest.current.onChange(out);
  };
  const setField = (f, v) => emit({ ...latest.current.value, [f]: v });

  const onPincode = (raw) => {
    const pin = raw.replace(/\D/g, '').slice(0, 6);
    setPinTouched(false);
    setNotFound(false);
    setField('address_pincode', pin);
    if (pin.length === 2) prefetchPincodeShard(pin);
    if (pin.length !== 6) return;
    lookupPincode(pin).then((hit) => {
      const { value: cur, srcMap: src, abhaValues: abha } = latest.current;
      if (!aliveRef.current || str(cur?.address_pincode) !== pin) return; // stale
      if (!hit) { setNotFound(true); return; }
      const next = { ...cur };
      let changed = false;
      [['address_district', hit.district], ['address_state', hit.state]].forEach(([f, filled]) => {
        const v = str(cur?.[f]);
        const fillable = !v || (str(autoRef.current[f]) === v && !isAbhaOrigin(f, cur, src, abha));
        if (filled && fillable && v !== filled) { next[f] = filled; changed = true; }
        if (filled && fillable) autoRef.current[f] = filled;
      });
      if (changed) emit(next);
    });
  };

  const stateOptions = useMemo(() => toOptions(listStates()), []);
  const districtOptions = useMemo(() => toOptions(districts), [districts]);
  // District suggestions load lazily on focus (never on mount), once per state.
  const loadDistricts = () => {
    const st = str(latest.current.value?.address_state);
    if (!st || districtsForRef.current === st) return;
    districtsForRef.current = st;
    listDistricts(st).then((list) => {
      if (aliveRef.current && districtsForRef.current === st) setDistricts(Array.isArray(list) ? list : []);
    });
  };
  const curState = str(value?.address_state);
  const shownDistricts = districtsForRef.current === curState ? districtOptions : [];

  const isOpen = mode === 'open' || expanded;
  useEffect(() => {
    if (!expanded || !pinInputRef.current) return;
    pinInputRef.current.focus();
    pinInputRef.current.scrollIntoView({ block: 'nearest' });
  }, [expanded]);

  const tid = (s) => `${testIdPrefix}-${s}`;
  const pin = str(value?.address_pincode);
  const pinError = pinTouched && pin !== '' && !PINCODE_RE.test(pin);
  const allEmpty = ADDRESS_FIELDS.every((f) => !str(value?.[f]));

  const title = (
    <div className="flex items-center gap-2 mb-1.5 min-h-[24px]">
      <span className={titleCls}>Address</span>
      {mode === 'collapsed' && expanded && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="ml-auto text-xs font-semibold px-3 py-1 rounded-lg border border-slate-500 bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700"
          data-testid={tid('done')}
        >
          Done
        </button>
      )}
    </div>
  );

  if (!isOpen) {
    const lines = formatAddressLines({ ...value, address: legacyAddress });
    // Legacy one-liner (all empty, or seeded into Address line by the host) shows in full: wraps, never cut (QA-ADDR-6).
    const wrapLine1 = allEmpty || (!!legacyAddress && value?.address_line === legacyAddress);
    return (
      <div data-testid={tid('block')}>
        {title}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setExpanded(true)}
          className={`${inputCls} flex items-center gap-3 text-left hover:border-teal-500 disabled:hover:border-slate-300`}
          data-testid={tid('row')}
        >
          {lines ? (
            <span className="flex-1 min-w-0 flex flex-col gap-0.5">
              <span className={`font-semibold ${wrapLine1 ? 'whitespace-normal break-words' : 'truncate'}`} data-testid={tid('summary-line1')}>{lines.line1}</span>
              {lines.line2 && (
                <span className="text-xs text-slate-500 dark:text-slate-400 truncate" data-testid={tid('summary-line2')}>{lines.line2}</span>
              )}
            </span>
          ) : (
            <span className="flex-1 text-slate-500 dark:text-slate-400">Add address (optional)</span>
          )}
          <span className="flex-none text-xs font-semibold px-2.5 py-1 rounded-full border-[1.5px] border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-950 dark:border-teal-400 dark:text-teal-300">
            {lines ? 'Edit' : '+ Add'}
          </span>
        </button>
      </div>
    );
  }

  const renderField = (f) => {
    const ui = FIELD_UI[f];
    const id = `${idPrefix}-${ui.slug}`;
    const mark = markFor(f);
    const v = value?.[f] ?? '';
    let input;
    if (f === 'address_pincode') {
      input = (
        <input
          ref={pinInputRef}
          id={id}
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoComplete="off"
          className={`${inputCls} ${pinError ? 'border-red-500 dark:border-red-500' : ''}`}
          value={v}
          placeholder={ui.placeholder}
          disabled={disabled}
          aria-invalid={pinError || undefined}
          onChange={(e) => onPincode(e.target.value)}
          onBlur={() => setPinTouched(true)}
          data-testid={tid(f)}
        />
      );
    } else if (f === 'address_district' || f === 'address_state') {
      input = (
        <div onFocus={f === 'address_district' ? loadDistricts : undefined}>
          <RegisterCombobox
            id={id}
            testId={tid(f)}
            value={v}
            onChange={(next) => setField(f, next)}
            options={f === 'address_state' ? stateOptions : shownDistricts}
            placeholder={ui.placeholder}
            disabled={disabled}
            maxShown={LIST_CAP}
            maxLength={ui.max}
          />
        </div>
      );
    } else {
      input = (
        <input
          id={id}
          type="text"
          className={inputCls}
          value={v}
          placeholder={ui.placeholder}
          maxLength={ui.max}
          disabled={disabled}
          onChange={(e) => setField(f, e.target.value)}
          data-testid={tid(f)}
        />
      );
    }
    return (
      <div key={f} className={`min-w-0 ${ui.span}`}>
        <label htmlFor={id} className={subLabelCls}>
          {ui.label}
          {mark === 'ABHA' && (
            <span className="text-[11px] font-semibold leading-tight text-gray-500 dark:text-gray-400" data-testid={tid(`src-${f}`)}>ABHA</span>
          )}
          {mark === 'Edited' && (
            <span className="text-[11px] font-semibold leading-tight text-amber-700 dark:text-amber-400" data-testid={tid(`src-${f}`)}>Edited</span>
          )}
        </label>
        {input}
        {f === 'address_pincode' && pinError && (
          <div className="text-[11.5px] text-red-600 dark:text-red-400 mt-1" data-testid={tid('pincode-error')}>{PINCODE_ERROR}</div>
        )}
        {f === 'address_pincode' && notFound && (
          <div className={hintCls} data-testid={tid('pincode-not-found')}>Pincode not found — fill district and state</div>
        )}
        {f === 'address_line' && str(legacyAddress) && (
          <div className={hintCls} data-testid={tid('legacy-hint')}>{LEGACY_HINT}</div>
        )}
      </div>
    );
  };

  return (
    <div className="[container-type:inline-size]" data-testid={tid('block')}>
      {title}
      <div className="grid grid-cols-2 [@container(min-width:400px)]:grid-cols-6 gap-3" data-testid={tid('grid')}>
        {LAYOUT.map(renderField)}
      </div>
      {hasMarks && <div className={hintCls} data-testid={tid('abdm-note')}>{ABDM_NOTE}</div>}
    </div>
  );
}
